// ============================================================================
// pdfCsatolas.js — PDF-melléklet előkészítése a kliensen (2026-10-10, tagi kérés: kis PDF-ek, pl. számlák).
//
// A feltöltés előtti ellenőrzések (a szerver és a Storage-szabály is ellenőriz, de a felhasználó itt kap
// érthető hibát): a fájl PDF (a típus vagy a .pdf végződés), legfeljebb PDF_MAX_BYTES, és a tartalma
// valóban "%PDF-" fejléccel kezdődik (a böngésző által jelzett típus önmagában megbízhatatlan).
// A feltöltést a meglévő uploadImage végzi (imageUpload.js), PDF-es típussal és kiterjesztéssel.
// ============================================================================

export const PDF_MAX_BYTES = 2 * 1024 * 1024;

/** PDF-nek tűnik-e a fájl (típus vagy kiterjesztés alapján)? A kép-úttól ez dönti el az elágazást. */
export function pdfE(file) {
  if (!file) return false;
  if (file.type === "application/pdf") return true;
  return typeof file.name === "string" && /\.pdf$/i.test(file.name) && (!file.type || file.type === "application/octet-stream");
}

/** A PDF fájlfejléce: "%PDF-" (25 50 44 46 2D). */
export function pdfFejlecE(bajtok) {
  return Boolean(bajtok) && bajtok.length >= 5 && bajtok[0] === 0x25 && bajtok[1] === 0x50 && bajtok[2] === 0x44 && bajtok[3] === 0x46 && bajtok[4] === 0x2d;
}

async function elsoBajtok(file, db = 5) {
  const szelet = file.slice(0, db);
  if (typeof szelet.arrayBuffer === "function") return new Uint8Array(await szelet.arrayBuffer());
  return new Promise((resolve, reject) => {
    const olvaso = new FileReader();
    olvaso.onload = () => resolve(new Uint8Array(olvaso.result));
    olvaso.onerror = () => reject(olvaso.error);
    olvaso.readAsArrayBuffer(szelet);
  });
}

/**
 * @returns {Promise<{ok: true, kiegeszito: {file, name, size}} | {ok: false, hibaKulcs: string}>}
 *   hibaKulcs: "file.tooBig" (túl nagy) | "file.invalid" (nem valódi PDF)
 */
export async function elokeszitPdf(file) {
  if (!file || !pdfE(file)) return { ok: false, hibaKulcs: "file.invalid" };
  if (!Number.isFinite(file.size) || file.size < 1) return { ok: false, hibaKulcs: "file.invalid" };
  if (file.size > PDF_MAX_BYTES) return { ok: false, hibaKulcs: "file.tooBig" };
  let bajtok;
  try {
    bajtok = await elsoBajtok(file);
  } catch (e) {
    return { ok: false, hibaKulcs: "file.invalid" };
  }
  if (!pdfFejlecE(bajtok)) return { ok: false, hibaKulcs: "file.invalid" };
  return { ok: true, kiegeszito: { file, name: String(file.name || "dokumentum.pdf").slice(0, 120), size: file.size } };
}

/** "183 kB" / "1,4 MB" - a megjelenítéshez. */
export function fajlMeret(bajt) {
  if (!Number.isFinite(bajt) || bajt < 0) return "";
  if (bajt < 1024 * 1024) return `${Math.max(1, Math.round(bajt / 1024))} kB`;
  return `${(bajt / (1024 * 1024)).toFixed(1).replace(".", ",")} MB`;
}

/** Biztonságos, https-es megnyitható hivatkozás egy üzenet fájljához, vagy null. */
export function fajlHivatkozas(msg) {
  const f = msg && msg.file;
  if (!f || typeof f.url !== "string" || !/^https:\/\//i.test(f.url)) return null;
  return { url: f.url, name: typeof f.name === "string" && f.name ? f.name : "dokumentum.pdf", size: Number(f.size) || 0 };
}
